import { useEffect, useMemo, useState } from 'react';
import { addDays, getDay, startOfWeek } from 'date-fns';
// eslint-disable-next-line no-duplicate-imports -- FIXME
import { format as fnsFormat } from 'date-fns';
import { enUS } from 'date-fns/locale';
import uniq from 'lodash/uniq';
import {
  selectIsBlockDeliveryEnabled,
  selectModuleConfigClientPanel,
  selectWeekDays,
  useAppConfigSelector,
} from '@hooks/useAppConfigSelectors';
import { format, formatArray } from '@services/Date.service';

const useBlockDelivery = ({ daysLeftToChoose, remainingDaysCount } = {}) => {
  const isBlockDeliveryEnabled = useAppConfigSelector(
    selectIsBlockDeliveryEnabled
  );
  const { validateBagChangesWithSlots = true } = useAppConfigSelector(
    selectModuleConfigClientPanel
  );

  const weekDaysSettings = useAppConfigSelector(selectWeekDays);
  const [disabledBlockDeliveryWeekDays, setDisabledBlockDeliveryWeekDays] =
    useState([]);

  useEffect(() => {
    // eslint-disable-next-line no-use-before-define -- FIXME
    const newDisabledBlockDeliveryWeekdDays = getDisabledWeekdays();

    setDisabledBlockDeliveryWeekDays(newDisabledBlockDeliveryWeekdDays);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
  }, [daysLeftToChoose, remainingDaysCount]);

  const areWeekendsDeliveredInBlock = useMemo(
    () =>
      weekDaysSettings.saturday.shouldBeDeliveredInBlock !== 6 ||
      weekDaysSettings.sunday.shouldBeDeliveredInBlock !== 7,
    [weekDaysSettings]
  );

  const blocksFromConfig = arr => {
    const b = [];
    let prev;

    arr.sort();
    // eslint-disable-next-line @typescript-eslint/prefer-for-of, no-plusplus -- FIXME
    for (let i = 0; i < arr.length; i++) {
      // eslint-disable-next-line no-negated-condition -- FIXME
      if (arr[i] !== prev) {
        b.push(1);
      } else {
        // eslint-disable-next-line no-plusplus -- FIXME
        b[b.length - 1]++;
      }
      prev = arr[i];
    }

    return uniq(b);
  };

  const blockSizes = blocksFromConfig(
    Object.values(weekDaysSettings)
      .filter(settings => typeof settings === 'object')
      .map(settings => settings.shouldBeDeliveredInBlock)
  );

  const getDisabledWeekdays = () => {
    if (
      !isBlockDeliveryEnabled ||
      daysLeftToChoose >= 7 ||
      daysLeftToChoose === undefined
    ) {
      return [];
    }

    let currentlyAvailableBlockSizes =
      // eslint-disable-next-line no-use-before-define -- FIXME
      getCurrentlyAvailableBlockSizes(daysLeftToChoose);
    const smallestAvailableBlockSize = Math.min(
      ...currentlyAvailableBlockSizes
    );
    // eslint-disable-next-line no-use-before-define -- FIXME
    const currentWeek = getSelectedDayWeekDates(new Date());

    if (
      remainingDaysCount < smallestAvailableBlockSize &&
      blockSizes.includes(remainingDaysCount)
    ) {
      currentlyAvailableBlockSizes = [remainingDaysCount];
    }

    const newDisabledWeekdays = currentWeek
      .filter(day => {
        // eslint-disable-next-line no-use-before-define -- FIXME
        const blockSize = getBlockDays(day)?.length;

        return !currentlyAvailableBlockSizes?.includes(blockSize);
      })
      .map(day => getDay(day));

    return newDisabledWeekdays;
  };

  const getDaySettings = day => {
    const weekDayName = fnsFormat(day, 'EEEE', { locale: enUS });

    return weekDaysSettings[weekDayName.toLowerCase()];
  };

  const checkIfDayIsDeliveredInBlock = day => {
    if (!isBlockDeliveryEnabled) {
      return false;
    }

    const daySettings = getDaySettings(day) ?? {};

    const shouldBeDeliveredInBlockSelectedDay =
      daySettings?.shouldBeDeliveredInBlock;

    return shouldBeDeliveredInBlockSelectedDay > 0;
  };

  const getSelectedDayWeekDates = day => {
    const firstDayOfWeek = startOfWeek(new Date(day), {
      weekStartsOn: 1,
    });

    let currentDate = firstDayOfWeek;
    let selectedDayWeekDates = [firstDayOfWeek];

    while (selectedDayWeekDates.length < 7) {
      currentDate = addDays(new Date(currentDate), 1);

      selectedDayWeekDates = [...selectedDayWeekDates, currentDate];
    }

    return selectedDayWeekDates;
  };

  const getBlockDays = day => {
    const selectedDaySettings = getDaySettings(day);
    const weekDatesToCheck = getSelectedDayWeekDates(day);

    return weekDatesToCheck.filter(date => {
      const dateSettings = getDaySettings(date);

      return (
        dateSettings?.shouldBeDeliveredInBlock ===
        selectedDaySettings?.shouldBeDeliveredInBlock
      );
    });
  };

  const getUpdatedBlockDates = ({ clickedDay, deliveryDates } = {}) => {
    const blockDays = getBlockDays(clickedDay);
    const formatedDates = formatArray(deliveryDates);
    const formatedBlockDays = formatArray(blockDays);

    // Removing day
    if (formatedDates.includes(format(clickedDay))) {
      return [
        ...deliveryDates.filter(
          deliveryDate => !formatedBlockDays.includes(format(deliveryDate))
        ),
      ];
    }

    // Adding day
    return [...deliveryDates, ...blockDays];
  };

  const getCurrentlyAvailableBlockSizes = (currentDaysAmount, depth = 0) => {
    if (currentDaysAmount === 0) {
      return [];
    }
    const blocksSum = blockSizes.reduce((a, b) => a + b, 0);
    const result = [];

    if (currentDaysAmount > 13) {
      return blockSizes;
    }

    if (currentDaysAmount % blocksSum === 0) {
      return blockSizes;
    }

    const currentDaysModuloSumPlusSum =
      (currentDaysAmount % blocksSum) + blocksSum;
    if (currentDaysAmount > blocksSum) {
      blockSizes.forEach(block => {
        if (
          currentDaysModuloSumPlusSum % block === 0 ||
          currentDaysAmount % block === 0
        ) {
          result.push(block);

          if (depth < 5) {
            // eslint-disable-next-line camelcase -- FIXME
            blockSizes.forEach(testowany_blok => {
              // eslint-disable-next-line camelcase -- FIXME
              if (testowany_blok !== block) {
                result.push(
                  ...getCurrentlyAvailableBlockSizes(
                    currentDaysAmount - block,
                    // eslint-disable-next-line no-plusplus, no-param-reassign -- FIXME
                    ++depth
                  )
                );
              }
            });
          }
        }
      });
    } else {
      blockSizes.forEach(block => {
        if (currentDaysAmount % block === 0) {
          result.push(block);
        }
      });
    }
    blockSizes.forEach(block => {
      blockSizes.forEach(blockToTest => {
        if (blockToTest !== block) {
          let lastTestedLength;
          let multiplier = 1;
          do {
            // eslint-disable-next-line no-shadow -- FIXME
            const lastTestedLength =
              currentDaysAmount - multiplier * blockToTest;

            if (lastTestedLength > 0 && lastTestedLength % block === 0) {
              result.push(block);
              result.push(blockToTest);
            }
            // eslint-disable-next-line no-plusplus -- FIXME
            multiplier++;
            // eslint-disable-next-line no-unmodified-loop-condition -- FIXME
          } while (lastTestedLength > 0);
        }
      });
    });

    return uniq(result);
  };

  return {
    getBlockDays,
    getUpdatedBlockDates,
    isBlockDeliveryEnabled,
    areWeekendsDeliveredInBlock,
    validateBagChangesWithSlots,
    checkIfDayIsDeliveredInBlock,
    disabledBlockDeliveryWeekDays,
    getCurrentlyAvailableBlockSizes,
  };
};

export default useBlockDelivery;
